import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchAssetDetail,
  clearSearch,
  setActiveSection,
  setAssetType,
  executeSearch,
} from '@/features/search/searchSlice';
import { seekToSeconds, setClipEndBoundary } from '@/features/player/playerSlice';
import { openDetailDrawer } from '@/features/ui/uiSlice';
import { assetToSearchMediaItem } from '@/services/tabFeedService';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import MediaTile from '@/components/content/MediaTile/MediaTile';
import SearchFilterPanel from '@/components/search/SearchFilterPanel/SearchFilterPanel';
import type { AssetCard, AssetTypeFilter, SectionSlug } from '@/types';
import styles from './SearchResults.module.css';

const ASSET_TYPE_OPTIONS: { value: AssetTypeFilter | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'video', label: 'Videos' },
  { value: 'image', label: 'Images' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cleanName(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
}

// ─── Skeleton tile — mirrors MediaTile's 280x157 (16:9) footprint ────────────

function SkeletonCard() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={styles.skeletonThumb} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SearchResults() {
  const dispatch = useAppDispatch();
  const { query, results, total, loading, activeAsset, loadingAssetId, sections_summary, activeSection, activeModeTagIds, availableTags, activeAssetType } =
    useAppSelector((s) => s.search);

  // The results panel sits at the very top of the page, so when a search
  // starts (loading flips true) scroll the window up — otherwise results that
  // just appeared above the current scroll position stay off-screen.
  useEffect(() => {
    if (loading) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loading]);

  // Only render when there's a meaningful query
  if (query.length < 2) return null;

  const handleSelect = (card: AssetCard) => {
    trackEvent(ANALYTICS_EVENTS.SEARCH_RESULT_CLICK, { assetId: card.asset_id, query, matchLabel: card.match_label });
    dispatch(setClipEndBoundary(null));

    // Work out where in the clip to start playback from — the matched
    // moment/clip if we have one, otherwise the beginning.
    let seekSeconds = 0;
    let clipEndSeconds: number | null = null;

    if (card.best_match) {
      // engram engine — prefer the unified best_match over the legacy fields below
      if (card.best_match.type === 'clip' || card.best_match.type === 'moment') {
        seekSeconds = card.best_match.timestamp_ms / 1000;
        if (card.best_match.type === 'clip') {
          clipEndSeconds = card.best_match.end_ms / 1000;
        }
      }
      // field_match / detail (image results) → no representative timestamp, plays from 0
    } else if (card.matched_moments && card.matched_moments.length > 0) {
      // legacy engine fallback — no best_match on the card
      // Active tag IDs whose search_mode is 'moment' (scene / object / activity)
      const momentTagIds = activeModeTagIds.filter((id) => {
        const tag = availableTags.find((t) => t.id === id);
        return tag?.search_mode === 'moment';
      });

      // Try to find a moment matching the active filter; fall back to index 0
      const match =
        momentTagIds.length > 0
          ? (card.matched_moments.find((m) => m.timestamp_ms != null && momentTagIds.includes(m.type)) ??
             card.matched_moments[0])
          : card.matched_moments[0];

      if (match?.timestamp_ms != null) {
        seekSeconds = match.timestamp_ms / 1000;
      }
    }
    // matched_moments null / empty → no seek, plays from 0

    // seekToSeconds also flips isPlaying true — the drawer picks this up and
    // opens straight into the playing video instead of the paused thumbnail.
    dispatch(seekToSeconds(seekSeconds));
    if (clipEndSeconds != null) dispatch(setClipEndBoundary(clipEndSeconds));

    const index = results.findIndex((c) => c.asset_id === card.asset_id);
    dispatch(openDetailDrawer(assetToSearchMediaItem(card, index)));
    dispatch(fetchAssetDetail({ assetId: card.asset_id, query, contentType: card.content_type }));
  };

  const handleClear = () => {
    trackEvent(ANALYTICS_EVENTS.SEARCH_CLEAR);
    dispatch(clearSearch());
  };

  const handleSectionFilter = (slug: SectionSlug | null) => {
    trackEvent(ANALYTICS_EVENTS.SEARCH_SECTION_FILTER, { section: slug });
    dispatch(setActiveSection(slug));
    dispatch(executeSearch({ query, limit: 15 }));
  };

  const handleAssetTypeFilter = (assetType: AssetTypeFilter | null) => {
    trackEvent(ANALYTICS_EVENTS.SEARCH_ASSET_TYPE_FILTER, { assetType: assetType ?? 'both' });
    dispatch(setAssetType(assetType));
    dispatch(executeSearch({ query, limit: 15 }));
  };

  // "Now showing" / "matched clips" describe an asset the user opened *from
  // this result set*. activeAsset is intentionally kept across searches (so the
  // last video stays loaded), so gate these banners on it actually being one of
  // the current results — otherwise a zero-result or refined search would keep
  // showing stale text from a previously-opened asset. (HP-UI banner bug)
  const activeInResults =
    activeAsset != null && results.some((c) => c.asset_id === activeAsset.asset_id);

  const noResults = !loading && results.length === 0;

  const activeClipCount = activeAsset?.matched_clips?.length ?? activeAsset?.matched_segments?.length ?? 0;

  return (
    <section className={styles.section} aria-label="Search results">
      {/* Header row */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {loading ? (
            <span className={styles.headerLabel}>Searching…</span>
          ) : (
            <span className={styles.headerLabel}>
              <b>{total}</b> result{total !== 1 ? 's' : ''} for{' '}
              <span className={styles.headerQuery}>"{query}"</span>
            </span>
          )}
          {activeInResults && !loading && (
            <span className={styles.headerNow}>
              · Now showing: <b>{cleanName(activeAsset!.file_name)}</b>
            </span>
          )}
        </div>
        <button className={styles.clearBtn} onClick={handleClear} aria-label="Clear search">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
          Clear
        </button>
      </div>

      {/* Asset type filter chips — video / image / both */}
      <div className={styles.filterChips} role="group" aria-label="Filter by asset type">
        {ASSET_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            className={`${styles.chip} ${activeAssetType === opt.value ? styles.chipActive : ''}`}
            onClick={() => handleAssetTypeFilter(opt.value)}
            aria-pressed={activeAssetType === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Section filter chips */}
      {sections_summary.length > 0 && (
        <div className={styles.filterChips} role="group" aria-label="Filter by section">
          {sections_summary.map((s) => (
            <button
              key={s.slug}
              className={`${styles.chip} ${activeSection === s.slug ? styles.chipActive : ''}`}
              onClick={() => handleSectionFilter(activeSection === s.slug ? null : s.slug as SectionSlug)}
              aria-pressed={activeSection === s.slug}
            >
              {s.label}
              <span className={styles.chipCount}>{s.match_count}</span>
            </button>
          ))}
          {activeSection && (
            <button
              className={styles.chipClear}
              onClick={() => handleSectionFilter(null)}
              aria-label="Clear section filter"
            >
              ✕ All
            </button>
          )}
        </div>
      )}

      {/* Advanced filters — source, date, duration, size, speaker */}
      <SearchFilterPanel />

      {/* Cards row — horizontal scroll — same MediaTile used everywhere else in the app */}
      {noResults ? (
        <div className={styles.empty} role="status">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <p className={styles.emptyTitle}>
            No results for <span className={styles.headerQuery}>"{query}"</span>
          </p>
          <p className={styles.emptyHint}>Try a different phrase, or clear the search to browse your library.</p>
        </div>
      ) : (
        <div className={styles.row} role="list">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : results.map((card, index) => (
                <MediaTile
                  key={card.asset_id}
                  item={assetToSearchMediaItem(card, index)}
                  onOpen={() => handleSelect(card)}
                />
              ))}
        </div>
      )}

      {/* Matched clips / segments hint */}
      {activeInResults && !loadingAssetId && activeClipCount > 0 && (
        <div className={styles.segmentsHint}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4l3 3" />
          </svg>
          {activeClipCount} matched clip
          {activeClipCount !== 1 ? 's' : ''} found — click transcript lines to jump
        </div>
      )}
    </section>
  );
}
